"use client";

import { useState } from "react";
import {
  Checkbox,
  CopyButton,
  SelectField,
} from "@/components/tool-kit/primitives";
import { BUTTON_PRIMARY, HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseColor } from "@/lib/tool-kit/color-charts/color-parse";
import {
  type Rgb,
  toHex,
  toHslString,
  toRgbString,
} from "@/lib/tool-kit/color-charts/color-space";
import { contrastRatio } from "@/lib/tool-kit/color-charts/contrast";
import {
  createCryptoRng,
  generatePalette,
  HARMONIES,
  type Harmony,
  MAX_COLORS,
} from "@/lib/tool-kit/color-charts/palette-gen";

/** English strings. A translated content file overrides these through `ui`. */
export const RANDOM_COLOR_GENERATOR_STRINGS = {
  harmonyLabel: "Color rule",
  harmonyHint:
    "A rule fixes the hues relative to a random base hue on the RGB color wheel. “Any color” picks each color independently from all 16.7 million.",
  harmonyAny: "Any color — fully random",
  harmonyMonochromatic: "Monochromatic — one hue, dark to light",
  harmonyAnalogous: "Analogous — neighbors within 60°",
  harmonyComplementary: "Complementary — opposite, 180°",
  harmonySplitComplementary: "Split-complementary — 150° and 210°",
  harmonyTriadic: "Triadic — three hues 120° apart",
  harmonyTetradic: "Tetradic — four hues 90° apart",
  countLabel: "Number of colors",
  generate: "Generate colors",
  generateHint:
    "Colors are drawn with your browser’s cryptographic random number generator when you press the button. Lock a color to keep it; the rule then builds around the first locked color.",
  results: "Your colors",
  lock: "Lock {hex}",
  textOn: "{hex} text",
  contrastWhite: "Contrast with white {ratio}:1",
  contrastBlack: "Contrast with black {ratio}:1",
  copyHex: "Copy HEX",
  copyRgb: "Copy RGB",
  copyHsl: "Copy HSL",
  copyAllHex: "Copy all HEX codes",
  copyCss: "Copy as CSS variables",
  copied: "Copied!",
  announce: "New palette of {count}: {list}.",
  swatchLabel: "Color {hex}",
  locked: "locked",
} as const;

type Strings = typeof RANDOM_COLOR_GENERATOR_STRINGS;

interface Slot {
  color: Rgb;
  locked: boolean;
}

const HARMONY_LABEL: Record<Harmony, keyof Strings> = {
  any: "harmonyAny",
  monochromatic: "harmonyMonochromatic",
  analogous: "harmonyAnalogous",
  complementary: "harmonyComplementary",
  splitComplementary: "harmonySplitComplementary",
  triadic: "harmonyTriadic",
  tetradic: "harmonyTetradic",
};

/** The Okabe–Ito colors: a fixed starting palette, so the server and browser agree. */
const START = ["#0072b2", "#e69f00", "#009e73", "#d55e00", "#cc79a7"];

function initialSlots(): Slot[] {
  return START.map((hex) => ({
    color: parseColor(hex) ?? { r: 0, g: 0, b: 0, a: 1 },
    locked: false,
  }));
}

function cssVariables(slots: readonly Slot[]): string {
  const lines = slots.map(
    (slot, index) => `  --color-${index + 1}: ${toHex(slot.color)};`
  );
  return `:root {\n${lines.join("\n")}\n}`;
}

function Swatch({
  slot,
  strings,
  onLock,
}: {
  slot: Slot;
  strings: Strings;
  onLock: (locked: boolean) => void;
}) {
  const fmt = useToolNumber();
  const hex = toHex(slot.color);
  const white = contrastRatio(slot.color, { r: 255, g: 255, b: 255, a: 1 });
  const black = contrastRatio(slot.color, { r: 0, g: 0, b: 0, a: 1 });
  const ink = white >= black ? "#ffffff" : "#000000";
  const two = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
  return (
    <li className="overflow-hidden rounded-lg border border-border bg-card">
      <div
        aria-label={t(strings.swatchLabel, { hex })}
        className="flex h-28 items-end justify-between p-3 font-mono text-sm"
        role="img"
        style={{ backgroundColor: hex, color: ink }}
      >
        <span>{hex.toUpperCase()}</span>
        {slot.locked ? <span>{strings.locked}</span> : null}
      </div>
      <div className="space-y-3 p-3">
        <p className="break-all font-mono text-foreground text-xs">
          {toRgbString(slot.color)}
          <br />
          {toHslString(slot.color)}
        </p>
        <p className={HINT}>
          {t(strings.contrastWhite, {
            ratio: fmt(Math.floor(white * 10) / 10, two),
          })}
          {" · "}
          {t(strings.contrastBlack, {
            ratio: fmt(Math.floor(black * 10) / 10, two),
          })}
        </p>
        <Checkbox
          checked={slot.locked}
          label={t(strings.lock, { hex })}
          onChange={onLock}
        />
        <div className="flex flex-wrap gap-2">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyHex}
            small
            text={hex}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyRgb}
            small
            text={toRgbString(slot.color)}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyHsl}
            small
            text={toHslString(slot.color)}
          />
        </div>
      </div>
    </li>
  );
}

export default function RandomColorGeneratorWidget() {
  const strings = useToolUi(RANDOM_COLOR_GENERATOR_STRINGS);
  const fmt = useToolNumber();
  const [slots, setSlots] = useState<Slot[]>(initialSlots);
  const [harmony, setHarmony] = useState<Harmony>("any");
  const [announcement, setAnnouncement] = useState("");

  function regenerate(nextHarmony: Harmony, count: number, from: Slot[]) {
    const palette = generatePalette({
      harmony: nextHarmony,
      count,
      locked: Array.from({ length: count }, (_, index) =>
        from[index]?.locked ? from[index].color : null
      ),
      rng: createCryptoRng(),
    });
    setSlots(
      palette.map((color, index) => ({
        color,
        locked: from[index]?.locked ?? false,
      }))
    );
    setAnnouncement(
      t(strings.announce, {
        count: fmt(palette.length),
        list: palette.map(toHex).join(", "),
      })
    );
  }

  function setLocked(index: number, locked: boolean) {
    setSlots(
      slots.map((slot, i) => (i === index ? { ...slot, locked } : slot))
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          hint={strings.harmonyHint}
          id="rcg-harmony"
          label={strings.harmonyLabel}
          onChange={(value) => {
            setHarmony(value);
            regenerate(value, slots.length, slots);
          }}
          options={HARMONIES.map((value) => ({
            value,
            label: strings[HARMONY_LABEL[value]],
          }))}
          value={harmony}
        />
        <SelectField
          id="rcg-count"
          label={strings.countLabel}
          onChange={(value) => regenerate(harmony, Number(value), slots)}
          options={Array.from({ length: MAX_COLORS }, (_, index) => ({
            value: String(index + 1),
            label: fmt(index + 1),
          }))}
          value={String(slots.length)}
        />
      </div>
      <div className="space-y-2">
        <button
          className={BUTTON_PRIMARY}
          onClick={() => regenerate(harmony, slots.length, slots)}
          type="button"
        >
          {strings.generate}
        </button>
        <p className={HINT}>{strings.generateHint}</p>
      </div>
      <section className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {slots.map((slot, index) => (
            <Swatch
              // Slots are positional: a lock belongs to the place, not the hex.
              // biome-ignore lint/suspicious/noArrayIndexKey: positional slots
              key={index}
              onLock={(locked) => setLocked(index, locked)}
              slot={slot}
              strings={strings}
            />
          ))}
        </ul>
        <div className="flex flex-wrap gap-3">
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyAllHex}
            text={slots.map((slot) => toHex(slot.color)).join(", ")}
          />
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyCss}
            text={cssVariables(slots)}
          />
        </div>
      </section>
    </div>
  );
}
